import { createContext, useCallback, useContext, useMemo, useState } from 'react';

type Theme = 'light' | 'dark';

const THEME_KEY = 'gw2-metas-theme-v1';

interface ThemeState {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeState | null>(null);

function persistTheme(theme: Theme) {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    // noop - storage might be unavailable
  }
}

function applyThemeClass(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.setAttribute('data-theme', 'dark');
  } else {
    root.removeAttribute('data-theme');
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const saved = localStorage.getItem(THEME_KEY) as Theme | null;
      if (saved === 'dark' || saved === 'light') {
        applyThemeClass(saved);
        return saved;
      }
      // system preference fallback
      const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      const initialTheme = prefersDark ? 'dark' : 'light';
      applyThemeClass(initialTheme);
      return initialTheme;
    } catch {
      applyThemeClass('light');
      return 'light';
    }
  });

  const updateTheme = useCallback((nextTheme: Theme | ((prev: Theme) => Theme)) => {
    setThemeState(prev => {
      const resolvedTheme = typeof nextTheme === 'function'
        ? (nextTheme as (prev: Theme) => Theme)(prev)
        : nextTheme;

      if (resolvedTheme === prev) {
        return prev;
      }

      applyThemeClass(resolvedTheme);
      persistTheme(resolvedTheme);
      return resolvedTheme;
    });
  }, []);

  const toggleTheme = useCallback(() => {
    updateTheme(current => (current === 'dark' ? 'light' : 'dark'));
  }, [updateTheme]);

  const setTheme = useCallback((nextTheme: Theme) => {
    updateTheme(nextTheme);
  }, [updateTheme]);

  const api = useMemo<ThemeState>(() => ({
    theme,
    toggleTheme,
    setTheme,
  }), [theme, toggleTheme, setTheme]);

  return <ThemeContext.Provider value={api}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

